<!-- @format -->

<template>
    <div :class="[containerClass, addMargin ? 'mt-6 mb-2' : '']">
        <v-label v-if="label && showLabel" :class="labelClass">
            {{
        label
            }}
        </v-label>

        <v-text-field
            v-if="type === 'text' ||
        type === 'date' ||
        type === 'number'
        "
            v-model="value"
            :prepend-inner-icon="prependInnerIcon"
            :class="customClass"
            :error-messages="errors"
            :type="type"
            :placeholder="placeholder"
            :variant="variant"
            :label="label"
            :disabled="disabled"
            :color="color"
            :min="min"
            :max="max"
            hide-details
            class="bg-color-surface"
            :prefix="prefix"
            :readonly="readonly"
            @input="(e) => onChange(e.target.value)"
            :data-cy="cyIdentifier"
        />

        <v-textarea
            v-if="type === 'textarea'"
            v-model="value"
            :class="customClass"
            :disabled="disabled"
            :placeholder="placeholder"
            :error-messages="errors"
            :type="type"
            :rows="rows"
            :variant="variant"
            :label="label"
            :color="color"
            hide-details
            class="bg-color-surface"
            @input="(e) => onChange(e.target.value)"
            :data-cy="cyIdentifier"
        ></v-textarea>

        <v-select
            v-if="type === 'select'"
            attach
            :class="customClass"
            :disabled="disabled"
            :error="error"
            :error-messages="errors"
            :items="items"
            v-model="value"
            :variant="variant"
            :label="label"
            :item-title="itemTitle"
            :item-value="itemValue"
            :color="color"
            hide-details
            class="bg-color-surface"
            :prefix="prefix"
            :prepend-inner-icon="prependInnerIcon"
            :multiple="multiple"
            :chips="chips"
            :clearable="clearable"
            @update:model-value="(modelValue) => onChange(modelValue)"
            :data-cy="cyIdentifier"
        ></v-select>


        <v-select
            v-if="type === 'country'"
            attach
            :class="customClass"
            :disabled="disabled"
            :error="error"
            :error-messages="errors"
            :items="countries"
            v-model="country"
            :variant="variant"
            :label="$t('company.country.fe') + '*'"
            item-title="title"
            item-value="value"
            :color="color"
            hide-details
            class="bg-color-surface"
            :prefix="prefix"
            :prepend-inner-icon="prependInnerIcon"
            :multiple="multiple"
            :chips="chips"
            :clearable="clearable"
            @update:model-value="(modelValue) => onChange(modelValue)"
            :data-cy="cyIdentifier"
        ></v-select>

        <v-autocomplete
            v-if="type === 'autocomplete'"
            :class="class"
            :disabled="disabled"
            :error-messages="errors"
            v-model="value"
            :items="items"
            :variant="variant"
            :label="label"
            :item-title="itemTitle"
            :item-value="itemValue"
            :color="color"
            class="bg-color-surface"
            :prefix="prefix"
            :prepend-inner-icon="prependInnerIcon"
            @update:model-value="(modelValue) => onChange(modelValue)"
            :data-cy="cyIdentifier"
        ></v-autocomplete>

        <div
            v-if="type === 'color'"
        >
            <input
                v-model="value"
                class="rounded-xl"
                :class="class"
                type="color"
                :style="{ 'background-color': value, 'border': '1px solid #dadada' }"
                :disabled="disabled"
                @input="(e) => onChange(e.target.value)"
            :data-cy="cyIdentifier"
            />
        </div>

        <v-switch
            v-if="type === 'switch'"
            v-model="value"
            :color="color"
            hide-details
            inset
            :label="label"
            :disabled="disabled"
            :readonly="readonly"
            @update:model-value="(modelValue) => onChange(modelValue)"
            :data-cy="cyIdentifier"
        ></v-switch>

        <v-checkbox
            v-if="type === 'checkbox'"
            v-model="value"
            :class="class"
            :checked="checked"
            :disabled="disabled"
            :label="label"
            :color="color"
            hide-details
            density="comfortable"
            :readonly="readonly"
            @update:model-value="(modelValue) => onChange(modelValue)"
            :data-cy="cyIdentifier"
        ></v-checkbox>

        <div v-if="type === 'password'" class="input-password-wrapper">
            <v-text-field
                v-model="value"
                :prepend-inner-icon="prependInnerIcon"
                :class="class"
                :error-messages="errors"
                :type="!showPassword ? 'password' : 'text'"
                :placeholder="placeholder"
                :variant="variant"
                :label="label"
                :disabled="disabled"
                :color="color"
                :min="min"
                :max="max"
                hide-details
                class="bg-color-surface"
                :prefix="prefix"
                @input="(e) => onChange(e.target.value)"
                :data-cy="cyIdentifier"
            />
            <span class="icon-password-button opacity-6">
                <i
                    class="mdi mdi-18px"
                    :class="!showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click="showPassword = !showPassword"
                ></i>
            </span>
        </div>

        <template v-if="type === 'file'">
            <div class="border rounded-pill mb-2 input-file" :class="class">
                <div class="d-flex gap-2 align-center px-4 py-2 justify-center">
                    <v-icon v-if="prependInnerIcon" :icon="prependInnerIcon"></v-icon>
                    <span class="ml-2">{{ placeholder }}</span>
                    <input
                        class="opacity-0 position-absolute"
                        type="file"
                        :accept="props.accept"
                        @change="onChangeFile($event)"
                    />
                </div>
            </div>
            <progress v-if="(fileProgress < 100)" class="w-100 ma-0 pa-0" :value="fileProgress" max="100"></progress>
            <div v-if="file" class="mb-2">
                <v-chip v-for="(item, index) in file" :key="index" class="ma-2" color="primary">
                    {{ item.name }}
                </v-chip>
            </div>
        </template>

        <p v-if="error" class="text-error">{{ errorMessage }}</p>
    </div>
</template>

<script>
import { useField } from "vee-validate";
import { computed, onBeforeMount, readonly, ref, toRef, watch } from "vue";
import { getActiveLanguage } from 'laravel-vue-i18n'

export default {
    props: {
        name: {
            type: String,
            default: ''
        },
        items: {
            type: Object,
            default: [],
        },
        type: {
            type: String,
            default: "text",
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        variant: {
            type: String,
            default: "outlined",
        },
        rows: {
            type: Number,
            default: 4,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        value: {
            default: "",
        },
        readonly:{
            type: Boolean
        },
        min: {
            type: Number,
            default: null,
        },
        max: {
            type: Number,
            default: null,
        },
        checked: {
            type: Boolean,
            default: true,
        },
        prependInnerIcon: {
            type: String,
            default: "",
        },
        class: {
            type: String,
            default: "",
        },
        containerClass: {
            type: String,
            default: "w-100",
        },
        color: {
            type: String,
            default: "primary",
        },
        labelClass: {
            type: String,
            default: "mt-6 mb-2",
        },
        prefix: {
            type: String,
            default: null,
        },
        file: {
            type: Object,
            default: () => ({})
        },
        fileProgress: {
            type: Number,
            default: 100
        },
        itemTitle: {
            type: String,
            default: "title"
        },
        itemValue: {
            type: String,
            default: "value"
        },
        multiple: {
            type: Boolean,
            default: false
        },
        chips: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false,
        },
        accept: {
            type: String,
            default: "",
        },
        disableMargin: {
            type: Boolean,
            default: false
        },
        initialCountry: {
            type: Number,
            default: 2
        },
        useCountryErp: {
            type: Boolean,
            default: false,
        }
    },
    emits: ["update:value"],

    setup(props, { emit }) {
        const { type, disableMargin } = props;

        const { value, handleBlur, errors, errorMessage, ...rest } = useField(
            toRef(props, "name"),
            undefined
        );

        const customClass = ref(props.class || ''); 
        watch(value, (newValue) => {
            customClass.value = newValue === null || newValue === '' 
                ? `${props.class || ''} custom-label-color`.trim() 
                : props.class || '';
        })

        const onChange = (value) => {
            emit("update:value", value);
        };

        const file = ref('');
        const fileInit = toRef(props, 'file');
        watch(fileInit, (value) => {
            file.value = value;
        });

        const onChangeFile = ($event) => {
            file.value = $event.target.files;
            emit("update:value", file.value);
        }

        const showLabel = computed(
            () => ["file", "color"].includes(type)
        );

        const addMargin = computed(
            () => ["text", "date", "password", "number", "textarea", "select", "autocomplete", "country"].includes(type) && !disableMargin
        );

        const showPassword = ref(false)

        const cyIdentifier = props.name + '-field'

        const country = ref(null)
        const countries = ref([])

        onBeforeMount(async () => {
            import(`@cache/${getActiveLanguage()}/noyau_ref_pays.json`)
            .then((countryData) => {
                // Convert data to the expected format
                countries.value = Object.values(countryData).map((c) => ({
                    value: (props.useCountryErp) ? c.openerp_id : c.id,
                    title: c.libelle
                }))

                //Select `initialCountry` if available, otherwise select the first item
                country.value =  {
                    value: countries.value.find(c => c.value == props.initialCountry)?.value || null,
                    title: countries.value.find(c => c.value == props.initialCountry)?.title || null
                }
            })
            .catch((error) => {
                console.error("Error loading countries:", error)
            })
        })

        watch(country, (newCountry) => {
            customClass.value = newCountry === null || newCountry === '' 
                ? `${props.class || ''} custom-label-color`.trim() 
                : props.class || '';
        })

        return {
            value,
            errors,
            props,
            errorMessage,
            showLabel,
            addMargin,
            file,
            handleBlur,
            onChange,
            onChangeFile,
            showPassword,
            cyIdentifier,
            country,
            countries,
            customClass,
        };
    },
};
</script>
<style>
/* Global */
.v-field {
    --v-field-padding-start: 32px !important;
    --v-field-padding-end: 32px !important;
    --v-field-padding-top: 24px !important;
    --v-field-padding-bottom: 24px !important;
    --v-field-input-padding-top: 24px !important;
    --v-field-input-padding-bottom: 24px !important
}

.v-field-label {
    margin: 0px var(--v-field-padding-start);
}

.v-field__input {
    --v-input-padding-top: 0px !important;
}

.v-field__clearable {
    padding-top: var(--v-field-padding-top);
}

.v-field__prepend-inner {
    padding-top: var(--v-field-padding-top);
    padding-left: calc(var(--v-field-padding-start) - 12px);
}

.v-field__append-inner {
    padding-top: var(--v-field-padding-top);
}

/* Select */
.v-select .v-input__control {
    min-height: calc(var(--v-input-control-height) + 10px);
}

/* Avatar */
.v-avatar.v-avatar-border {
    height: calc(var(--v-avatar-height) + 4px) !important;
    width: calc(var(--v-avatar-height) + 4px) !important;
}

/* Checkbox */
.v-checkbox .v-input__control .v-label {
    margin: 0px;
}

/* Switch */
.v-switch label {
    margin: 0px;
}

/* Color */
input[type="color"] {
    -webkit-appearance: none;
    appearance: none;
    border: none;
    border-color: transparent;
    cursor: pointer;
}

input[type="color"]::-webkit-color-swatch-wrapper {
    padding: 0;
}

input[type="color"]::-webkit-color-swatch {
    border: none;
}

input[type="color"]:focus {
    outline: none;
}

/* File */
div.input-file {
    min-width: 200px;
    max-width: 250px;
}

/* Label */
.v-label {
    opacity: var(--v-high-emphasis-opacity) !important;
}

/* Password */
.input-password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.icon-password-button {
  position: absolute;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
}
.custom-label-color .v-label {
  color: #929c9f;
}
</style>
